import {
    UseMutationOptions,
    UseMutationResult,
    useMutation,
    useQueryClient,
} from "react-query";

import { PaymentPlatform } from "../../../utils/enums";
import { ICreateOrderResponse } from "../../types";
import { api } from "../../utils";
import { useAuth } from "../useAuth";
import { useMessage } from "../useMessage";

export interface IUseCreateOrderMutationParameters {
    customerId: string;
    billingCustomerAddressId: string;
    note?: string;
    shippingCustomerAddressId: string;
    test?: boolean;
    cartId: string;
    platform: PaymentPlatform;
}

/**
 * Create a new order. Calls `POST /api/v2/orders`.
 */
export const useCreateOrder = (
    invalidQueryKeys?: string | string[],
    options?: UseMutationOptions<
        ICreateOrderResponse,
        unknown,
        IUseCreateOrderMutationParameters,
        unknown
    >,
): UseMutationResult<
    ICreateOrderResponse,
    unknown,
    IUseCreateOrderMutationParameters
> => {
    const auth = useAuth();
    const queryClient = useQueryClient();
    const { showError } = useMessage();

    return useMutation<
        ICreateOrderResponse,
        unknown,
        IUseCreateOrderMutationParameters
    >({
        ...options,
        mutationFn: (
            parameters: IUseCreateOrderMutationParameters,
        ): Promise<ICreateOrderResponse> => {
            if (!auth.token) {
                throw new Error(
                    "Cannot create order without authorization token.",
                );
            }

            return api.v2.Orders.create({
                ...parameters,
                token: auth.token,
            });
        },
        onError: (error: any) => showError(error),
        onSuccess: (
            order: ICreateOrderResponse,
            variables: IUseCreateOrderMutationParameters,
            context: unknown,
        ) => {
            if (invalidQueryKeys !== undefined) {
                queryClient.invalidateQueries(invalidQueryKeys);
            }

            options?.onSuccess?.(order, variables, context);
        },
    });
};
